import { Injectable, inject, signal } from '@angular/core';
import { Auth, authState } from '../core/firebase/auth';
import {
  Observable,
  catchError,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { UserApiService } from './user-api.service';
import { markInternalDevice } from '../core/internal-traffic';

@Injectable({ providedIn: 'root' })
export class StaffService {
  private readonly userApi = inject(UserApiService);
  private readonly user$ = authState(inject(Auth));
  private readonly state = signal<boolean>(false);
  private cached: { uid: string; request$: Observable<boolean> } | null = null;

  readonly isStaff = this.state.asReadonly();

  /** Caches the answer per signed-in user; a logged-out check or a failed call is not kept. */
  ensureLoaded(): Observable<boolean> {
    return this.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          this.cached = null;
          this.state.set(false);
          return of(false);
        }
        if (this.cached?.uid !== user.uid) {
          this.cached = { uid: user.uid, request$: this.load(user.uid) };
        }
        return this.cached.request$;
      })
    );
  }

  private load(uid: string): Observable<boolean> {
    return this.userApi.getAccount().pipe(
      map(account => account.is_staff === true),
      tap(value => {
        this.state.set(value);
        if (value) markInternalDevice();
      }),
      catchError(() => {
        if (this.cached?.uid === uid) {
          this.cached = null;
        }
        this.state.set(false);
        return of(false);
      }),
      shareReplay(1)
    );
  }
}
